import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FileText, Coins, Eye, Edit, Trash, X, Search, XCircle, MoreVertical, Plus, ChevronDown, Check, CheckCircle, Send, CreditCard, Printer, RefreshCw, Files } from 'lucide-react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import InvoicePreview from '../components/InvoicePreview';
import AdhocInvoicePreview from '../components/AdhocInvoicePreview';
import CreditNoteModal from '../components/CreditNoteModal';
import {
  fetchInvoices,
  fetchClients,
  fetchFacilities,
  fetchConfig,
  verifyPasscode,
  deleteInvoice,
  addInvoicePayment, // Updated import
  updateInvoice,
  getCreditNotesForInvoice,
  cancelInvoice,
  uncancelInvoice,
  downloadInvoicePDF,
  approveInvoice,
  verifyInvoice,
  submitInvoiceForReview,
  rejectInvoice
} from '../utils/api';
import { formatIndianCurrency, formatIndianNumber, numberToWords } from '../utils/numberToWords';
import { UOM_OPTIONS } from '../utils/uom';
import HSNSearchDropdown from '../components/HSNSearchDropdown';
import CodeTypeSelect, { codeTypeFromIsServc, isServcFromCodeType } from '../components/CodeTypeSelect';
import { isViewer, getStoredUser } from '../utils/auth';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import { downloadGSTJSON } from '../utils/jsonGenerator';
import { Input, Select, InputNumber, DatePicker, Spin, message, Table, Tag, Tabs, Button, Modal, Card, Empty, Typography, Dropdown, Menu } from 'antd';
import dayjs from 'dayjs';


const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const YEARS = Array.from({ length: 2040 - 2023 + 1 }, (_, i) => 2023 + i);

function InvoiceHistory({ printRef, handlePrint, currentUser }) {
  // Data state - fetched from API
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [config, setConfig] = useState({});

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Safely format invoice numbers without duplicating financial year
  const formatInvoiceNumberSafe = (invNo, fy, cfg) => {
    try {
      if (typeof invNo === 'string' && /\d{4}-\d{2}\/\d+/.test(invNo)) {
        return invNo;
      }
      if (!fy || typeof fy !== 'string') {
        return invNo || 'N/A';
      }
      return formatInvoiceNumber(invNo, fy, cfg);
    } catch (e) {
      return invNo || 'N/A';
    }
  };

  // Get current date and month for default selections
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11 for Jan-Dec

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [excelMonth, setExcelMonth] = useState(new Date().getMonth());
  const [excelYear, setExcelYear] = useState(new Date().getFullYear());
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ receivedAmount: '', utrNumber: '', receivedDate: '', tdsPercent: '' });
  const [selectedMonth, setSelectedMonth] = useState(-1); // Default to show all months
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering invoices
  const [selectedInvoices, setSelectedInvoices] = useState(new Set()); // Set of selected invoice IDs
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    client: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    financialYear: '',
    poNumber: '',
    poDate: '',
    showShipTo: 'no',
    items: [],
    placeOfSupply: ''
  });
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('all'); // 'all', 'rent', 'adhoc', 'credit_note'
  const [selectedApprovalStatus, setSelectedApprovalStatus] = useState('all'); // 'all', 'draft', 'pending_review', 'verified', 'approved', 'rejected'
  const [selectedCreator, setSelectedCreator] = useState('all'); // 'all', 'my_invoices', userId, 'unassigned'
  const [rejectModalRecord, setRejectModalRecord] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [statusTab, setStatusTab] = useState('active'); // 'active' | 'cancelled'
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false); // Track month dropdown state
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false); // Track year dropdown state
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false);
  const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState(null);

  // Fetch all data from API
  const fetchAllData = useCallback(async (showRefreshing = false) => {
    console.log('[InvoiceHistory] fetchAllData called, showRefreshing:', showRefreshing);
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log('[InvoiceHistory] Starting API calls...');

      // Fetch all data in parallel
      const [invoicesData, clientsData, facilitiesData, configData] = await Promise.all([
        fetchInvoices(),
        fetchClients(),
        fetchFacilities(),
        fetchConfig()
      ]);

      console.log('[InvoiceHistory] API calls completed:', {
        invoices: invoicesData?.length || 0,
        clients: clientsData?.length || 0,
        facilities: facilitiesData?.length || 0,
        hasConfig: !!configData
      });

      setInvoices(invoicesData || []);
      setClients(clientsData || []);
      setFacilities(facilitiesData || []);
      setConfig(configData || {});

      console.log(`[InvoiceHistory] State updated - Loaded ${invoicesData?.length || 0} invoices, ${clientsData?.length || 0} clients`);
    } catch (err) {
      console.error('[InvoiceHistory] Error fetching data:', err);
      setError(err.message || 'Failed to load data');
      message.error('Failed to load invoice data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    console.log('[InvoiceHistory] Component mounted, calling fetchAllData...');
    fetchAllData();
  }, [fetchAllData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
      if (monthDropdownOpen && !event.target.closest('.month-dropdown-container')) {
        setMonthDropdownOpen(false);
      }
      if (yearDropdownOpen && !event.target.closest('.year-dropdown-container')) {
        setYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
    };
  }, [openDropdown, monthDropdownOpen, yearDropdownOpen]);

  // Financial year: e.g. '2024-2025'
  function getFinancialYear(date) {
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return null; // Invalid date
      const year = d.getFullYear();
      const month = d.getMonth();
      if (month < 3) {
        // Jan, Feb, Mar belong to previous FY
        return `${year - 1}-${year}`;
      } else {
        return `${year}-${year + 1}`;
      }
    } catch (error) {
      return null;
    }
  }

  const currentFY = getFinancialYear(new Date());
  const [selectedFY, setSelectedFY] = useState('all'); // Default to show all financial years

  // First, filter out invalid invoices (those without invoiceNumber) and non-Invoice objects
  const validInvoices = invoices.filter(inv => {
    // Check if this is actually an Invoice object
    const hasUserFields = inv.moduleAccess !== undefined ||
      inv.email !== undefined ||
      (inv.role !== undefined && !inv.invoiceNumber);

    const hasInvoiceFields = inv.invoiceNumber !== undefined ||
      inv.invoiceDate !== undefined ||
      inv.items !== undefined ||
      inv.totals !== undefined;

    // If it has User fields but no Invoice fields, it's not an Invoice
    if (hasUserFields && !hasInvoiceFields) {
      return false;
    }

    const invoiceNumberValue = inv.invoiceNumber;
    const hasInvoiceNumber = invoiceNumberValue !== null &&
      invoiceNumberValue !== undefined &&
      invoiceNumberValue !== '';

    return hasInvoiceNumber;
  });

  const activeUser = currentUser || getStoredUser();

  // Get all unique financial years from valid invoices
  const allFYs = Array.from(new Set(validInvoices.map(inv => {
    if (!inv.invoiceDate) return null;
    return getFinancialYear(inv.invoiceDate);
  }).filter(Boolean)));

  // Extract unique creators from validInvoices
  const uniqueCreators = useMemo(() => {
    const creatorMap = new Map();
    validInvoices.forEach(inv => {
      if (inv.creator && typeof inv.creator === 'object' && (inv.creator._id || inv.creator.id)) {
        const id = String(inv.creator._id || inv.creator.id);
        const name = inv.creator.name || inv.creator.email || 'Unnamed User';
        if (!creatorMap.has(id)) {
          creatorMap.set(id, { id, name });
        }
      }
    });
    if (activeUser && (activeUser._id || activeUser.id)) {
      const cId = String(activeUser._id || activeUser.id);
      if (!creatorMap.has(cId) && activeUser.name) {
        creatorMap.set(cId, { id: cId, name: activeUser.name });
      }
    }
    return Array.from(creatorMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [validInvoices, activeUser]);

  const hasUnassignedInvoices = useMemo(() => {
    return validInvoices.some(inv => !inv.creator);
  }, [validInvoices]);

  // Filter invoices by selected month, FY, and invoice type
  const filteredInvoices = validInvoices.filter(inv => {
    // Show all invoices (both active and cancelled)

    // Get invoice type (default to 'rent' for backward compatibility)
    const invoiceType = inv.invoiceType || 'rent';

    // Filter by invoice type
    if (selectedInvoiceType !== 'all' && invoiceType !== selectedInvoiceType) {
      return false;
    }

    // Filter by approval status (credit notes do not have approval status)
    if (selectedApprovalStatus !== 'all') {
      if (invoiceType === 'credit_note') return false;
      const appr = inv.approvalStatus || 'approved'; // legacy invoices treated as approved
      if (appr !== selectedApprovalStatus) {
        return false;
      }
    }

    // Filter by creator
    if (selectedCreator !== 'all') {
      const activeUserId = activeUser?._id || activeUser?.id;
      const invCreatorId = inv.creator
        ? (typeof inv.creator === 'object' ? (inv.creator._id || inv.creator.id) : inv.creator)
        : null;
      const invCreatorIdStr = invCreatorId ? String(invCreatorId) : null;

      if (selectedCreator === 'my_invoices') {
        if (!activeUserId || invCreatorIdStr !== String(activeUserId)) {
          return false;
        }
      } else if (selectedCreator === 'unassigned') {
        if (invCreatorIdStr) {
          return false;
        }
      } else {
        if (invCreatorIdStr !== String(selectedCreator)) {
          return false;
        }
      }
    }

    // If no invoiceDate, still show the invoice (don't filter by date)
    if (!inv.invoiceDate) {
      return true;
    }

    try {
      const d = new Date(inv.invoiceDate);
      if (isNaN(d.getTime())) {
        return true;
      }

      const invoiceMonth = d.getMonth();
      const invoiceFY = getFinancialYear(d);

      const monthMatches = selectedMonth === -1 || invoiceMonth === Number(selectedMonth);
      const fyMatches = selectedFY === 'all' ? true : (invoiceFY !== null && invoiceFY === selectedFY);

      return monthMatches && fyMatches;
    } catch (error) {
      return true;
    }
  });

  // Filter invoices by search term (invoice number, client name, UTR number)
  const searchFilteredInvoices = filteredInvoices.filter(inv => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase().trim();

    const invoiceNumberWithFY = formatInvoiceNumberSafe(inv.invoiceNumber, inv.financialYear || getFinancialYear(inv.invoiceDate), config);
    if (invoiceNumberWithFY.toLowerCase().includes(searchLower)) return true;

    const clientName = inv.client ? (inv.client.registeredName || inv.client.name || '') : '';
    if (clientName.toLowerCase().includes(searchLower)) return true;

    const utrNumber = inv.utrNumber || '';
    if (utrNumber.toLowerCase().includes(searchLower)) return true;

    return false;
  });

  // Sort filtered invoices by date + number (latest first)
  function extractInvoiceNumber(inv) {
    try {
      const num = String(inv.invoiceNumber || '').split('/').pop();
      return parseInt(num, 10) || 0;
    } catch (error) {
      return 0;
    }
  }
  function extractInvoiceDate(inv) {
    try {
      return inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : 0;
    } catch (error) {
      return 0;
    }
  }
  const sortedInvoices = [...searchFilteredInvoices].sort((a, b) => {
    const dateDiff = extractInvoiceDate(b) - extractInvoiceDate(a);
    if (dateDiff !== 0) return dateDiff;
    return extractInvoiceNumber(b) - extractInvoiceNumber(a);
  });

  // Reset pagination to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonth, selectedFY, selectedInvoiceType, selectedApprovalStatus, selectedCreator]);

  // Adjust current page if total pages decreases below current page
  useEffect(() => {
    const totalPages = Math.ceil(sortedInvoices.length / pageSize) || 1;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [sortedInvoices.length, pageSize, currentPage]);

  const handleRefresh = () => {
    fetchAllData(true);
  };

  const handleApproveInvoice = (record) => {
    Modal.confirm({
      title: 'Approve Invoice',
      content: `Are you sure you want to approve invoice #${record.displayInvoiceNumber}? Once approved, payments can be recorded.`,
      okText: 'Approve',
      okButtonProps: { style: { background: '#52c41a', borderColor: '#52c41a' } },
      onOk: async () => {
        try {
          await approveInvoice(record.invoiceId, { comment: 'Approved by admin' });
          message.success('Invoice approved successfully');
          fetchAllData(true);
        } catch (error) {
          console.error('Error approving invoice:', error);
          message.error(error.message || 'Failed to approve invoice');
        }
      }
    });
  };

  const handleVerifyInvoice = (record) => {
    Modal.confirm({
      title: 'Verify Invoice',
      content: `Verify invoice #${record.displayInvoiceNumber}? This moves it to verified status for final admin approval.`,
      okText: 'Verify',
      okType: 'primary',
      onOk: async () => {
        try {
          await verifyInvoice(record.invoiceId, { comment: 'Verified by reviewer' });
          message.success('Invoice verified successfully');
          fetchAllData(true);
        } catch (error) {
          console.error('Error verifying invoice:', error);
          message.error(error.message || 'Failed to verify invoice');
        }
      }
    });
  };

  const handleSubmitForReview = (record) => {
    Modal.confirm({
      title: 'Submit for Review',
      content: `Submit invoice #${record.displayInvoiceNumber} for review?`,
      okText: 'Submit',
      okType: 'primary',
      onOk: async () => {
        try {
          await submitInvoiceForReview(record.invoiceId, {});
          message.success('Invoice submitted for review successfully');
          fetchAllData(true);
        } catch (error) {
          console.error('Error submitting invoice for review:', error);
          message.error(error.message || 'Failed to submit invoice for review');
        }
      }
    });
  };

  const handleOpenRejectModal = (record) => {
    setRejectModalRecord(record);
    setRejectComment('');
  };

  const handleConfirmReject = async () => {
    if (!rejectComment.trim()) {
      message.error('Please enter a rejection reason');
      return;
    }
    try {
      setWorkflowLoading(true);
      await rejectInvoice(rejectModalRecord.invoiceId, { comment: rejectComment.trim() });
      message.success('Invoice rejected and returned for rework');
      setRejectModalRecord(null);
      setRejectComment('');
      fetchAllData(true);
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      message.error(error.message || 'Failed to reject invoice');
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    const passcode = window.prompt('Enter passcode to delete this invoice:');
    if (!passcode) return;
    try {
      const result = await verifyPasscode(passcode);
      if (!result.valid) {
        alert('Incorrect passcode. Invoice not deleted.');
        return;
      }

      await deleteInvoice(invoiceId);
      message.success('Invoice deleted successfully.');

      setInvoices(prev => prev.filter(invoice => (invoice._id || invoice.id) !== invoiceId));
      setSelectedInvoice(null);
      
      // Refresh the app after deletion
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      message.error('Failed to delete invoice. Please try again.');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice? This will exclude it from all exports and calculations.')) {
      return;
    }

    try {
      const passcode = window.prompt('Enter passcode to cancel this invoice:');
      if (!passcode) return;
      const result = await verifyPasscode(passcode);
      if (!result.valid) {
        alert('Incorrect passcode. Invoice not cancelled.');
        return;
      }
      await cancelInvoice(invoiceId);
      message.success('Invoice cancelled successfully');

      // Refresh data to get updated status
      fetchAllData(true);
    } catch (err) {
      message.error('Failed to cancel invoice. Please try again.');
    }
  };

  const toggleDropdown = (invoiceId) => {
    setOpenDropdown(openDropdown === invoiceId ? null : invoiceId);
  };

  const handleActionClick = (action, invoiceId) => {
    setOpenDropdown(null);

    switch (action) {
      case 'payment':
        handleOpenPaymentModal(invoices.find(inv => (inv._id || inv.id) === invoiceId));
        break;
      case 'view':
        setSelectedInvoice(invoices.find(inv => (inv._id || inv.id) === invoiceId));
        break;

      case 'edit':
        const invoiceToEdit = invoices.find(inv => (inv._id || inv.id) === invoiceId);

        setEditingInvoice(invoiceToEdit);
        setEditForm({
          client: invoiceToEdit.client._id || invoiceToEdit.client,
          invoiceNumber: invoiceToEdit.invoiceNumber,
          invoiceDate: invoiceToEdit.invoiceDate,
          financialYear: invoiceToEdit.financialYear,
          poNumber: invoiceToEdit.poNumber || '',
          poDate: invoiceToEdit.poDate || '',
          showShipTo: invoiceToEdit.showShipTo || 'no',
          items: invoiceToEdit.items || [],
          placeOfSupply: invoiceToEdit.placeOfSupply || ''
        });
        break;
      case 'delete':
        handleDeleteInvoice(invoiceId);
        break;
      case 'cancel':
        handleCancelInvoice(invoiceId);
        break;
      case 'uncancel':
        (async () => {
          try {
            const passcode = window.prompt('Enter passcode to revoke cancellation:');
            if (!passcode) return;
            const result = await verifyPasscode(passcode);
            if (!result.valid) { alert('Incorrect passcode.'); return; }
            const updated = await uncancelInvoice(invoiceId);
            setInvoices(prev => prev.map(inv => ((inv._id || inv.id) === invoiceId ? { ...inv, isCancelled: false, cancelledAt: undefined, cancelReason: undefined, editStatus: updated.editStatus || inv.editStatus } : inv)));
            message.success('Invoice reactivated.');
          } catch (e) {
            message.error('Failed to revoke cancellation.');
          }
        })();
        break;
      case 'credit_note':
        const invoiceForCredit = invoices.find(inv => (inv._id || inv.id) === invoiceId);
        if (invoiceForCredit.invoiceType === 'credit_note') {
          alert('Cannot create credit note for another credit note');
          return;
        }
        setSelectedInvoiceForCredit(invoiceForCredit);
        setCreditNoteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDownloadExcel = () => {
    setExcelModalOpen(true);
  };

  const handleExcelExport = () => {
    setExcelModalOpen(false);
    const filtered = invoices.filter(inv => {
      if (inv && inv.isCancelled) return false;
      if (!inv.invoiceDate) return false;

      try {
        const d = new Date(inv.invoiceDate);
        if (isNaN(d.getTime())) return false;

        return d.getMonth() === Number(excelMonth) && d.getFullYear() === Number(excelYear);
      } catch (error) {
        return false;
      }
    });

    const data = [];
    let srNo = 1;

    filtered.forEach((invoice) => {
      const isCredit = (invoice.invoiceType === 'credit_note');
      const isAdhoc = (invoice.invoiceType === 'adhoc');
      const receivedAmountNum = Number(invoice.receivedAmount || 0);
      const tdsPercentNum = Number(invoice.tdsPercent || 0);
      const subtotalAmountNum = Number(invoice.totals?.subtotal || 0);
      const adhocTdsTotal = Number(invoice.totals?.totalTds || 0);
      const tdsDeductionNum = isCredit ? 0 : (isAdhoc && adhocTdsTotal > 0 ? adhocTdsTotal : (subtotalAmountNum * (tdsPercentNum / 100)));

      let clientFacilityId = invoice.client?.facilityId;
      if (!clientFacilityId && invoice.client?._id) {
        const clientFromList = clients.find(c => String(c._id) === String(invoice.client._id));
        clientFacilityId = clientFromList?.facilityId;
      }
      const facilityName = clientFacilityId && facilities
        ? (facilities.find(fac => String(fac.id) === String(clientFacilityId))?.name || '')
        : '';

      const totals = invoice.totals || {};
      const invoiceSubtotal = isCredit
        ? Math.abs(Number(totals.subtotal || 0))
        : Number(totals.subtotal || 0);
      const invoiceCgstTotal = isCredit
        ? Math.abs(Number(totals.cgst || 0))
        : Number(totals.cgst || 0);
      const invoiceSgstTotal = isCredit
        ? Math.abs(Number(totals.sgst || 0))
        : Number(totals.sgst || 0);
      const invoiceTotal = isCredit
        ? Math.abs(Number(totals.total || 0))
        : Number(totals.total || 0);

      const inferPercent = (part, base) => {
        const b = Math.abs(Number(base || 0));
        if (!b) return 0;
        const p = Math.abs(Number(part || 0));
        return Math.round((p / b) * 10000) / 100;
      };

      const cgstPercentDefault = (config.cgstRate !== undefined) ? Number(config.cgstRate) : inferPercent(invoiceCgstTotal, invoiceSubtotal);
      const sgstPercentDefault = (config.sgstRate !== undefined) ? Number(config.sgstRate) : inferPercent(invoiceSgstTotal, invoiceSubtotal);

      const items = Array.isArray(invoice.items) && invoice.items.length > 0 ? invoice.items : [{}];
      const itemCount = items.length || 1;
      const isAdhocInvoice = invoice.invoiceType === 'adhoc';

      items.forEach((item, itemIdx) => {
        let taxable = 0;
        let cgstPercent = 0;
        let sgstPercent = 0;
        let cgstAmt = 0;
        let sgstAmt = 0;
        let totalAmt = 0;

        if (isAdhocInvoice || (
          (item.netAmount !== undefined && item.netAmount !== null && Number(item.netAmount) !== 0) ||
          (item.totalAmount !== undefined && item.totalAmount !== null && Number(item.totalAmount) !== 0) ||
          (item.tax !== undefined && item.tax !== null && Number(item.tax) !== 0) ||
          (item.taxAmount !== undefined && item.taxAmount !== null && Number(item.taxAmount) !== 0) ||
          (item.unitPrice !== undefined && item.unitPrice !== null && Number(item.unitPrice) !== 0)
        )) {
          const qtyNum = Number(item.qty || 1) || 1;
          const unitPrice = isCredit ? Math.abs(Number(item.unitPrice || 0)) : Number(item.unitPrice || 0);
          const netAmountProvided = isCredit ? Math.abs(Number(item.netAmount || 0)) : Number(item.netAmount || 0);
          taxable = netAmountProvided || (unitPrice * qtyNum);

          const totalTaxPercent = Number(item.tax || 0);
          const taxAmountProvided = isCredit ? Math.abs(Number(item.taxAmount || 0)) : Number(item.taxAmount || 0);
          const computedTaxAmount = totalTaxPercent ? (taxable * totalTaxPercent) / 100 : 0;
          const taxAmount = taxAmountProvided || computedTaxAmount;

          cgstPercent = totalTaxPercent ? totalTaxPercent / 2 : 0;
          sgstPercent = totalTaxPercent ? totalTaxPercent / 2 : 0;
          cgstAmt = taxAmount / 2;
          sgstAmt = taxAmount / 2;
          const itemTotalAmount = isCredit ? Math.abs(Number(item.totalAmount || 0)) : Number(item.totalAmount || 0);
          totalAmt = itemTotalAmount || (taxable + taxAmount);
        } else {
          let itemAmount = isCredit ? Math.abs(Number(item.amount || 0)) : Number(item.amount || 0);

          if (!itemAmount) {
            if (invoiceSubtotal > 0) {
              itemAmount = invoiceSubtotal / itemCount;
            } else if (invoiceTotal > 0 && (cgstPercentDefault > 0 || sgstPercentDefault > 0)) {
              const taxMultiplier = 1 + (cgstPercentDefault / 100) + (sgstPercentDefault / 100);
              const calculatedSubtotal = invoiceTotal / taxMultiplier;
              itemAmount = calculatedSubtotal / itemCount;
            }
          }

          taxable = itemAmount;
          cgstPercent = cgstPercentDefault;
          sgstPercent = sgstPercentDefault;

          cgstAmt = taxable * (cgstPercent / 100);
          sgstAmt = taxable * (sgstPercent / 100);
          totalAmt = taxable + cgstAmt + sgstAmt;
        }

        data.push({
          'Sr.No': srNo++,
          'Date': invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'dd/MM/yyyy') : '',
          'Name of the company': invoice.client?.registeredName || invoice.client?.name || '',
          'Facility Name': facilityName,
          'Company GSTIN': invoice.client?.gstin || '',
          'Invoice number': invoice.invoiceType === 'credit_note'
            ? (invoice.creditNoteNumber || invoice.invoiceNumber)
            : formatInvoiceNumberSafe(invoice.invoiceNumber, invoice.financialYear || getFinancialYear(invoice.invoiceDate), config),
          'Invoice Type': invoice.invoiceType || 'rent',
          'HSN/SAC': item.hsn || '',
          'Description': item.description || '',
          'UOM': item.uom || '',
          'Quantity': typeof item.qty === 'number' ? item.qty : 1,
          'Taxable': Math.round(taxable * 100) / 100,
          'CGST %': cgstPercent,
          'CGST': Math.round(cgstAmt * 100) / 100,
          'SGST %': sgstPercent,
          'SGST': Math.round(sgstAmt * 100) / 100,
          'Total': Math.round(totalAmt * 100) / 100,
          'UTR Number': isCredit ? '' : (invoice.utrNumber || ''),
          'TDS %': isCredit ? '' : (isAdhoc && (item.tdsPercent != null) ? item.tdsPercent : (tdsPercentNum || 0)),
          'TDS Deduction': isCredit ? '' : (isAdhoc && (item.tdsAmount != null) ? Math.round(Number(item.tdsAmount) * 100) / 100 : Math.round(tdsDeductionNum * 100) / 100),
          'Received Amount': isCredit ? '' : receivedAmountNum
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    XLSX.writeFile(wb, `invoice-detailed-${MONTHS[excelMonth]}-${excelYear}.xlsx`);
  };

  const handleInvoiceSelection = (invoiceId, isSelected) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        if (newSet.size >= 15) {
          alert('You can select maximum 15 invoices at once for JSON download.');
          return prev;
        }
        newSet.add(invoiceId);
      } else {
        newSet.delete(invoiceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.size === sortedInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      const allIds = sortedInvoices.map(inv => inv._id || inv.id);
      if (allIds.length > 15) {
        alert('Can only select maximum 15 invoices at once for JSON download. Selecting first 15 invoices.');
        setSelectedInvoices(new Set(allIds.slice(0, 15)));
      } else {
        setSelectedInvoices(new Set(allIds));
      }
    }
  };

  // Handle download of selected invoices (PDF or ZIP)
  const handleDownloadInvoices = async () => {
    if (selectedInvoices.size === 0) {
      alert('Please select at least one invoice to download.');
      return;
    }

    const invoicesToDownload = sortedInvoices.filter(invoice => selectedInvoices.has(invoice._id || invoice.id));
    const isMultiple = invoicesToDownload.length > 1;

    try {
      if (isMultiple) {
        message.loading(`Preparing ${invoicesToDownload.length} invoices for download...`, 0);
        const zip = new JSZip();

        // Process sequentially to avoid overwhelming the server
        for (const invoice of invoicesToDownload) {
          try {
            const blob = await downloadInvoicePDF(invoice._id || invoice.id);
            const invoiceNo = (invoice.invoiceNumber || 'unknown').replace(/\//g, '-');
            const clientName = (invoice.clientName || invoice.client?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
            zip.file(`Invoice-${invoiceNo}-${clientName}.pdf`, blob);
          } catch (err) {
            console.error(`Failed to download invoice ${invoice.invoiceNumber}:`, err);
          }
        }

        const content = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoices-${format(new Date(), 'yyyy-MM-dd-HHmm')}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.destroy();
        message.success('Invoices downloaded successfully');
      } else {
        // Single invoice download
        const invoice = invoicesToDownload[0];
        message.loading('Downloading invoice...', 0);
        const blob = await downloadInvoicePDF(invoice._id || invoice.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const invoiceNo = (invoice.invoiceNumber || 'invoice').replace(/\//g, '-');
        const clientName = (invoice.clientName || invoice.client?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
        link.download = `Invoice-${invoiceNo}-${clientName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.destroy();
        message.success('Invoice downloaded successfully');
      }
    } catch (error) {
      message.destroy();
      console.error('Download error:', error);
      message.error('Failed to download invoices. Please try again.');
    }
  };

  const handleDownloadJSON = async (invoicesArg) => {
    let invoicesToExport;
    if (Array.isArray(invoicesArg)) {
      invoicesToExport = invoicesArg;
    } else {
      if (selectedInvoices.size === 0) {
        alert('Please select at least one invoice to download.');
        return;
      }
      invoicesToExport = sortedInvoices.filter(invoice => selectedInvoices.has(invoice._id || invoice.id));
    }

    try {
      await downloadGSTJSON(invoicesToExport, config);
      message.success('GST JSON export initiated successfully.');
    } catch (error) {
      console.error('JSON Download Error:', error);
      message.error(error.message || 'Failed to download JSON files.');
    }
  };

  const handleOpenPaymentModal = (invoice) => {
    setPaymentInvoice(invoice);
    setPaymentForm({
      receivedAmount: '', // Clear for new payment
      utrNumber: '',
      receivedDate: dayjs().format('YYYY-MM-DD'), // Default to today
      tdsPercent: invoice.tdsPercent || '', // Pre-fill existing TDS %
      paymentMode: 'Bank Transfer',
      paymentNotes: ''
    });
    setPaymentModalOpen(true);
  };

  const handleSavePayment = async () => {
    try {
      const payload = {
        amount: paymentForm.receivedAmount,
        date: paymentForm.receivedDate,
        paymentMode: paymentForm.paymentMode,
        utrNumber: paymentForm.utrNumber,
        notes: paymentForm.paymentNotes,
        tdsPercent: paymentForm.tdsPercent
      };

      const updated = await addInvoicePayment(paymentInvoice._id || paymentInvoice.id, payload);
      setPaymentModalOpen(false);

      const idx = invoices.findIndex(inv => (inv._id || inv.id) === (updated._id || updated.id));
      if (idx !== -1) {
        const updatedInvoices = [...invoices];
        updatedInvoices[idx] = updated;
        setInvoices(updatedInvoices);
      }

      message.success(`Payment of ${formatIndianCurrency(Number(payload.amount))} added successfully!`);
    } catch (err) {
      console.error(err);
      message.error('Failed to add payment: ' + err.message);
    }
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditItemChange = (index, field, value) => {
    const updatedItems = [...editForm.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setEditForm(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const addEditItem = () => {
    const newItem = editingInvoice?.invoiceType === 'adhoc'
      ? { description: '', unitPrice: 0, qty: 1, uom: '', netAmount: 0, tax: 0, taxAmount: 0, totalAmount: 0, hsn: '', IsServc: 'N' }
      : { description: '', amount: 0, uom: '', hsn: '' };

    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeEditItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateEditTotals = () => {
    if (editingInvoice?.invoiceType === 'adhoc') {
      const totalNetAmount = editForm.items.reduce((sum, item) => {
        const unitPrice = Number(item.unitPrice || 0);
        const qty = Number(item.qty || 1);
        return sum + (unitPrice * qty);
      }, 0);

      const totalTaxAmount = editForm.items.reduce((sum, item) => {
        const unitPrice = Number(item.unitPrice || 0);
        const qty = Number(item.qty || 1);
        const netAmount = unitPrice * qty;
        const taxRate = Number(item.tax || 0);
        return sum + (netAmount * (taxRate / 100));
      }, 0);

      const grossTotal = totalNetAmount + totalTaxAmount;
      const roundedTotal = Math.round(grossTotal);
      const roundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;

      return {
        subtotal: totalNetAmount,
        cgst: totalTaxAmount / 2,
        sgst: totalTaxAmount / 2,
        roundOff,
        total: roundedTotal
      };
    } else {
      const subtotal = editForm.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const cgstRate = config && config.cgstRate !== undefined ? config.cgstRate : 0;
      const sgstRate = config && config.sgstRate !== undefined ? config.sgstRate : 0;
      const cgst = subtotal * (cgstRate / 100);
      const sgst = subtotal * (sgstRate / 100);
      const grossTotal = subtotal + cgst + sgst;
      const roundedTotal = Math.round(grossTotal);
      const roundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;

      return { subtotal, cgst, sgst, roundOff, total: roundedTotal };
    }
  };

  const handleEditSubmit = async () => {
    try {
      // HSN (goods) items require a UOM; SAC (service) items make it optional
      if (editingInvoice.invoiceType !== 'rent') {
        const missingUomIdx = editForm.items.findIndex(it => (it.IsServc || 'N') !== 'Y' && !it.uom);
        if (missingUomIdx !== -1) {
          message.warning(`Row ${missingUomIdx + 1}: UOM is required for items with HSN Code.`);
          return;
        }
      }
      const { subtotal, cgst, sgst, roundOff, total } = calculateEditTotals();
      const creatorId = (() => {
        if (!editingInvoice?.creator) {
          return currentUser?._id || null;
        }
        if (typeof editingInvoice.creator === 'object') {
          return editingInvoice.creator?._id || currentUser?._id || null;
        }
        return editingInvoice.creator;
      })();

      const updatedInvoice = {
        ...editForm,
        items: editingInvoice.invoiceType === 'rent'
          ? editForm.items.map(item => ({ ...item, IsServc: 'Y' }))
          : editForm.items,
        totals: { subtotal, cgst, sgst, roundOff, total },
        amountInWords: numberToWords(total),
        invoiceType: editingInvoice.invoiceType,
        creator: creatorId
      };

      const result = await updateInvoice(editingInvoice._id, updatedInvoice);

      setInvoices(prev => prev.map(inv =>
        (inv._id || inv.id) === editingInvoice._id ? result : inv
      ));

      setEditingInvoice(null);
      setEditForm({
        client: '',
        invoiceNumber: '',
        invoiceDate: '',
        financialYear: '',
        poNumber: '',
        poDate: '',
        showShipTo: 'no',
        items: [],
        placeOfSupply: ''
      });

      message.success('Invoice updated successfully!');
      
      // Refresh the app after update
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      console.error('Error updating invoice:', error);
      message.error(`Failed to update invoice: ${errorMessage}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading invoices..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2 style={{ color: '#e74c3c' }}>Error Loading Data</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => fetchAllData()}>
            <RefreshCw size={16} style={{ marginRight: 8 }} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Invoice / Credit History</h1>
          <p>View and manage all generated invoices and credit notes</p>
        </div>
        <div className="page-actions">
          <div className="invoice-history-filters" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>


            {/* Month Dropdown */}
            <Select
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              style={{ width: 160 }}
              options={[
                { value: -1, label: 'Show All Months' },
                ...MONTHS.map((month, index) => ({ value: index, label: month }))
              ]}
            />

            {/* Year Dropdown */}
            <Select
              value={selectedFY}
              onChange={(value) => setSelectedFY(value)}
              style={{ width: 160 }}
              size="medium"
              options={[
                { value: 'all', label: 'Show All Years' },
                ...(allFYs.length > 0 ? allFYs.map(fy => ({ value: fy, label: fy })) : [{ value: currentFY, label: currentFY }])
              ]}
            />

            {/* Approval Status Dropdown */}
            <Select
              value={selectedApprovalStatus}
              onChange={(value) => setSelectedApprovalStatus(value)}
              style={{ width: 160 }}
              options={[
                { value: 'all', label: 'All Approvals' },
                { value: 'draft', label: 'Draft' },
                { value: 'pending_review', label: 'Pending Review' },
                { value: 'verified', label: 'Verified' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
              ]}
            />

            {/* Created By Dropdown */}
            <Select
              value={selectedCreator}
              onChange={(value) => setSelectedCreator(value)}
              style={{ width: 160 }}
              options={[
                { value: 'all', label: 'All Creators' },
                ...(activeUser && (activeUser._id || activeUser.id) ? [{ value: 'my_invoices', label: 'My Invoices' }] : []),
                ...uniqueCreators.map(c => ({ value: c.id, label: c.name })),
                ...(hasUnassignedInvoices ? [{ value: 'unassigned', label: 'Unassigned' }] : [])
              ]}
            />

            {/* Excel Download Button */}
            <Button
              onClick={handleDownloadExcel}
              icon={<span role="img" aria-label="chart">📊</span>}
            >
              Download Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Search Box */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ position: 'relative', maxWidth: '400px', flex: 1 }} className='custom-search'>
          <Input
            prefix={<Search size={16} style={{ color: '#999' }} />}
            placeholder="Search by invoice number, client name, or UTR..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>

        {selectedInvoices.size > 0 && (
          <div style={{ display: 'flex', gap: '12px', marginLeft: '16px' }}>
            <Button
              type="primary"
              onClick={handleDownloadInvoices}
              icon={<DownloadOutlined />}
              style={{ backgroundColor: '#0f766e', borderColor: '#0f766e' }}
            >
              Download Invoice ({selectedInvoices.size})
            </Button>
            <Button
              onClick={handleDownloadJSON}
              icon={<FileTextOutlined />}
            >
              Download GST JSON ({selectedInvoices.size} selected)
            </Button>
          </div>
        )}
      </div>

      {/* Invoice Type Tabs */}
      {/* Invoice Type Tabs */}
      <div style={{ marginBottom: 0 }}>
        <Tabs
          activeKey={selectedInvoiceType}
          onChange={(key) => setSelectedInvoiceType(key)}

          items={[
            {
              key: 'all',
              label: `All (${validInvoices.length})`,
            },
            {
              key: 'rent',
              label: `Rent Invoice (${validInvoices.filter(inv => (inv.invoiceType || 'rent') === 'rent').length})`,
            },
            {
              key: 'adhoc',
              label: `Adhoc Invoice (${validInvoices.filter(inv => (inv.invoiceType || 'rent') === 'adhoc').length})`,
            },
            {
              key: 'credit_note',
              label: `Credit Note (${validInvoices.filter(inv => (inv.invoiceType || 'rent') === 'credit_note').length})`,
            },
          ]}
        />
      </div>

      {/* <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.9em', color: '#666' }}>
        Showing {sortedInvoices.length} of {filteredInvoices.length} invoices
        {selectedMonth !== -1 && ` for ${MONTHS[selectedMonth]}`}
        {selectedFY !== 'all' && ` in FY ${selectedFY}`}
        {selectedInvoiceType !== 'all' && ` (${selectedInvoiceType === 'rent' ? 'Rent' : selectedInvoiceType === 'adhoc' ? 'Adhoc' : 'Credit Note'} invoices)`}
        {searchTerm.trim() && ` matching "${searchTerm}"`}
      </div> */}



      {/* Excel Download Modal */}
      <Modal
        title="Download Excel Report"
        open={excelModalOpen}
        onCancel={() => setExcelModalOpen(false)}
        onOk={handleExcelExport}
        okText="Download"
        cancelText="Cancel"
      >
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Select the month and year for which you want to generate the detailed invoice report.
          The downloaded Excel file will contain comprehensive details including taxable amounts, GST breakdown, and TDS information for all invoices in the selected period.
        </p>

        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Month</label>
          <Select
            style={{ width: '100%' }}
            value={excelMonth}
            onChange={(v) => setExcelMonth(v)}
            options={MONTHS.map((m, i) => ({ label: m, value: i }))}
          />
        </div>

        <div className="form-group">
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Year</label>
          <Select
            style={{ width: '100%' }}
            value={excelYear}
            onChange={(v) => setExcelYear(v)}
            options={YEARS.map(y => ({ label: y, value: y }))}
          />
        </div>
      </Modal>

      {/* Invoice Table */}
      {sortedInvoices.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          background: '#fff',
          borderRadius: 8,
        }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Typography.Text strong style={{ fontSize: 16 }}>No Invoices Found</Typography.Text>
                <Typography.Text type="secondary">
                  {validInvoices.length === 0
                    ? 'No invoices in the database yet. Create your first invoice to get started.'
                    : invoices.length > 0 && validInvoices.length === 0
                      ? `Found ${invoices.length} invoices, but all are missing required fields.`
                      : `No invoices match the current filter criteria. Try adjusting the month, year, or invoice type filters.`
                  }
                </Typography.Text>
              </div>
            }
          />
        </div>
      ) : (
        <>
          <Card
            styles={{ body: { padding: 0 } }}
            style={{ borderRadius: 8 }}
          >
            <Table
              dataSource={sortedInvoices.map(invoice => {
                const invoiceId = invoice._id || invoice.id;
                let total = invoice.totals?.total || 0;
                if (total === 0 && invoice.items && invoice.items.length > 0) {
                  if (invoice.invoiceType === 'adhoc') {
                    total = invoice.items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
                  } else {
                    total = invoice.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                  }
                }

                let displayInvoiceNumber = 'N/A';
                if (invoice.invoiceType === 'credit_note') {
                  displayInvoiceNumber = invoice.creditNoteNumber || invoice.invoiceNumber || 'N/A';
                } else if (invoice.invoiceNumber) {
                  const formatted = formatInvoiceNumberSafe(invoice.invoiceNumber, invoice.financialYear || getFinancialYear(invoice.invoiceDate), config);
                  displayInvoiceNumber = formatted !== 'N/A' ? formatted : invoice.invoiceNumber;
                }

                let displayDate = 'Invalid Date';
                if (invoice.invoiceDate) {
                  try {
                    const dateObj = typeof invoice.invoiceDate === 'string' ? new Date(invoice.invoiceDate) : invoice.invoiceDate;
                    if (!isNaN(dateObj.getTime())) {
                      displayDate = format(dateObj, 'dd/MM/yyyy');
                    }
                  } catch (e) { }
                }

                let clientName = 'Unknown Client';
                if (invoice.client) {
                  if (typeof invoice.client === 'object') {
                    clientName = invoice.client.registeredName || invoice.client.name || 'Unknown Client';
                  } else if (clients && Array.isArray(clients)) {
                    const clientId = String(invoice.client);
                    const foundClient = clients.find(c => String(c._id || c.id) === clientId);
                    if (foundClient) {
                      clientName = foundClient.registeredName || foundClient.name || 'Unknown Client';
                    }
                  }
                }

                let creatorName = 'Unknown User';
                if (invoice.creator && typeof invoice.creator === 'object') {
                  creatorName = invoice.creator.name || 'Unknown User';
                }

                return {
                  key: invoiceId,
                  invoiceId,
                  displayInvoiceNumber,
                  invoiceType: invoice.invoiceType,
                  isCancelled: invoice.isCancelled,
                  receivedAmount: invoice.receivedAmount,
                  tdsPercent: invoice.tdsPercent,
                  dueDate: invoice.dueDate,
                  total,
                  displayDate,
                  clientName,
                  creatorName,
                  approvalStatus: invoice.approvalStatus,
                  assignedTo: invoice.assignedTo,
                  creator: invoice.creator,
                  invoice
                };
              })}
              rowSelection={{
                selectedRowKeys: Array.from(selectedInvoices),
                onChange: (selectedRowKeys) => {
                  setSelectedInvoices(new Set(selectedRowKeys));
                }
              }}
              columns={[
                {
                  title: 'Invoice No',
                  dataIndex: 'displayInvoiceNumber',
                  key: 'invoiceNo',
                  width: 130,
                  render: (text, record) => (
                    <Link
                      to={`/invoice/${record.invoiceId}`}
                      style={{ color: '#2563eb', fontWeight: 'bold', textDecoration: 'none' }}
                    >
                      {text}
                    </Link>
                  )
                },
                {
                  title: 'Type',
                  dataIndex: 'invoiceType',
                  key: 'type',
                  width: 90,
                  render: (type) => {
                    const colors = { adhoc: '#ff6b35', credit_note: '#2563eb', rent: '#4facfe' };
                    const labels = { adhoc: 'Adhoc', credit_note: 'Credit', rent: 'Rent' };
                    return (
                      <Tag color={colors[type] || '#4facfe'} style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {labels[type] || 'Rent'}
                      </Tag>
                    );
                  }
                },
                {
                  title: 'Status',
                  key: 'status',
                  width: 120,
                  render: (_, record) => {
                    if (record.invoiceType === 'credit_note') {
                      return null;
                    }
                    if (record.isCancelled) {
                      return <Tag color="default">Cancelled</Tag>;
                    }
                    const cashReceived = Number(record.receivedAmount || 0);
                    const invTotal = Number(record.total || 0);
                    const subtotal = Number(record.invoice?.totals?.subtotal || 0);
                    const adhocTds = record.invoiceType === 'adhoc' ? Number(record.invoice?.totals?.totalTds || 0) : 0;

                    // Apply TDS logic: 10% for rent; totals.totalTds for adhoc
                    const netExpected = record.invoiceType === 'rent'
                      ? (invTotal - (subtotal * 0.10))
                      : (adhocTds > 0 ? (invTotal - adhocTds) : invTotal);

                    const isPaid = cashReceived >= (netExpected - 1) && invTotal > 0; // -1 for small rounding diffs
                    const isPartiallyPaid = cashReceived > 0 && cashReceived < (netExpected - 1);
                    const isOverdue = !isPaid && !isPartiallyPaid && record.dueDate && new Date() > new Date(record.dueDate);

                    if (isPaid) return <Tag color="green">Paid</Tag>;
                    if (isPartiallyPaid) return <Tag color="orange">Partially Paid</Tag>;
                    if (isOverdue) return <Tag color="red">Overdue</Tag>;
                    return <Tag color="blue">Unpaid</Tag>;
                  }
                },
                {
                  title: 'Approval',
                  key: 'approvalStatus',
                  width: 130,
                  render: (_, record) => {
                    if (record.invoiceType === 'credit_note') {
                      return <span style={{ color: '#aaa' }}>—</span>;
                    }
                    const status = record.approvalStatus || 'approved';
                    const configs = {
                      draft: { color: 'default', text: 'Draft' },
                      pending_review: { color: 'orange', text: 'Pending Review' },
                      verified: { color: 'cyan', text: 'Verified' },
                      approved: { color: 'green', text: 'Approved' },
                      rejected: { color: 'red', text: 'Rejected' }
                    };
                    const cfg = configs[status] || { color: 'default', text: status };
                    return (
                      <Tag color={cfg.color} style={{ fontWeight: 500 }}>
                        {cfg.text}
                      </Tag>
                    );
                  }
                },
                {
                  title: 'Client',
                  dataIndex: 'clientName',
                  key: 'client',
                  ellipsis: true
                },
                {
                  title: 'Date',
                  dataIndex: 'displayDate',
                  key: 'date',
                  width: 110
                },
                {
                  title: '₹ Total',
                  key: 'total',
                  width: 130,
                  render: (_, record) => {
                    const adhocTds = record.invoiceType === 'adhoc' ? Number(record.invoice?.totals?.totalTds || 0) : 0;
                    return (
                      <div>
                        {formatIndianCurrency(Number(record.total))}
                        {record.tdsPercent ? (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                            TDS: {record.tdsPercent}%
                          </div>
                        ) : (adhocTds > 0 ? (
                          <div style={{ fontSize: '11px', color: '#cf1322', marginTop: '2px' }}>
                            TDS: -{formatIndianCurrency(adhocTds)}
                          </div>
                        ) : null)}
                      </div>
                    );
                  }
                },
                {
                  title: 'Created By',
                  dataIndex: 'creatorName',
                  key: 'createdBy',
                  width: 130,
                  ellipsis: true
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 80,
                  fixed: 'right',
                  render: (_, record) => {
                    const canMutate = !isViewer();
                    const isAdminUser = currentUser?.role === 'admin';
                    const creatorId = record.creator?._id || record.creator?.id || record.creator;
                    const currentUserId = currentUser?._id || currentUser?.id;
                    const isCreator = creatorId && currentUserId && String(creatorId) === String(currentUserId);
                    const appStatus = record.approvalStatus || 'approved'; // legacy invoices treated as approved
                    const isAppr = record.invoiceType === 'credit_note' || appStatus === 'approved';
                    const isReviewState = appStatus === 'pending_review' || appStatus === 'verified';

                    const menuItems = [
                      {
                        key: 'view',
                        label: 'View Invoice',
                        icon: <Eye size={14} />,
                        onClick: () => handleActionClick('view', record.invoiceId)
                      },
                      (record.invoiceType !== 'credit_note' && !record.isCancelled) && {
                        key: 'download',
                        label: 'Download PDF',
                        icon: <DownloadOutlined />,
                        onClick: () => {
                          message.loading('Generating PDF...', 1);
                          downloadInvoicePDF(record.invoiceId).then(blob => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `Invoice-${record.displayInvoiceNumber}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }).catch(err => message.error('Failed to download PDF'));
                        }
                      },

                      // Approval workflow actions
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && (appStatus === 'draft' || appStatus === 'rejected')) && {
                        key: 'submit_review',
                        label: 'Submit for Review',
                        icon: <Send size={14} style={{ color: '#1890ff' }} />,
                        onClick: () => handleSubmitForReview(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && (appStatus === 'draft' || appStatus === 'rejected') && isAdminUser) && {
                        key: 'approve_direct',
                        label: 'Approve Invoice',
                        icon: <CheckCircle size={14} style={{ color: '#059669' }} />,
                        onClick: () => handleApproveInvoice(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && appStatus === 'pending_review' && (!isCreator || isAdminUser)) && {
                        key: 'verify_review',
                        label: 'Verify Invoice',
                        icon: <Check size={14} style={{ color: '#2563eb' }} />,
                        onClick: () => handleVerifyInvoice(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && appStatus === 'pending_review' && isAdminUser) && {
                        key: 'approve_pending',
                        label: 'Approve Invoice',
                        icon: <CheckCircle size={14} style={{ color: '#059669' }} />,
                        onClick: () => handleApproveInvoice(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && appStatus === 'pending_review' && (!isCreator || isAdminUser)) && {
                        key: 'reject_pending',
                        label: 'Reject Invoice',
                        icon: <XCircle size={14} style={{ color: '#dc2626' }} />,
                        danger: true,
                        onClick: () => handleOpenRejectModal(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && appStatus === 'verified' && isAdminUser) && {
                        key: 'approve_verified',
                        label: 'Approve Invoice',
                        icon: <CheckCircle size={14} style={{ color: '#059669' }} />,
                        onClick: () => handleApproveInvoice(record)
                      },
                      (canMutate && record.invoiceType !== 'credit_note' && !record.isCancelled && appStatus === 'verified' && isAdminUser) && {
                        key: 'reject_verified',
                        label: 'Reject Invoice',
                        icon: <XCircle size={14} style={{ color: '#dc2626' }} />,
                        danger: true,
                        onClick: () => handleOpenRejectModal(record)
                      },

                      canMutate && !isReviewState && {
                        key: 'edit',
                        label: 'Edit',
                        icon: <Edit size={14} />,
                        onClick: () => handleActionClick('edit', record.invoiceId)
                      },
                      canMutate && (record.invoiceType !== 'credit_note' && !record.isCancelled && isAppr) && {
                        key: 'payment',
                        label: 'Receive Payment',
                        icon: <Coins size={14} />,
                        onClick: () => handleActionClick('payment', record.invoiceId)
                      },
                      canMutate && (record.invoiceType !== 'credit_note' && !record.isCancelled) && {
                        key: 'credit_note',
                        label: 'Create Credit Note',
                        icon: <FileText size={14} />,
                        onClick: () => handleActionClick('credit_note', record.invoiceId)
                      },
                      canMutate && (record.isCancelled ? {
                        key: 'uncancel',
                        label: 'Reactivate',
                        icon: <RefreshCw size={14} />,
                        onClick: () => handleActionClick('uncancel', record.invoiceId)
                      } : (record.invoiceType !== 'credit_note' && {
                        key: 'cancel',
                        label: 'Cancel Invoice',
                        icon: <XCircle size={14} />,
                        danger: true,
                        onClick: () => handleActionClick('cancel', record.invoiceId)
                      })),
                      canMutate && { type: 'divider' },
                      canMutate && {
                        key: 'delete',
                        label: 'Delete',
                        icon: <Trash size={14} />,
                        danger: true,
                        onClick: () => handleActionClick('delete', record.invoiceId)
                      }
                    ].filter(Boolean);

                    return (
                      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                        <Button
                          type="text"
                          icon={<MoreVertical size={18} />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    );
                  }
                }
              ]}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                style: { padding: '12px 16px', margin: 0 }
              }}
              size="middle"
              scroll={{ x: 800 }}
            />
          </Card>

          {/* Invoice Preview Modal */}
          {selectedInvoice && (
            <div className="modal-overlay" style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <div className="modal-content" style={{
                background: '#fff', borderRadius: '10px', padding: 0, maxWidth: '900px', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                maxHeight: '90vh', overflowY: 'auto'
              }}
                onClick={e => e.stopPropagation()}
              >
                <div className="no-print" style={{ position: 'sticky', top: 0, left: 0, right: 0, background: '#fff', zIndex: 5, padding: '10px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    {selectedInvoice.invoiceType === 'credit_note'
                      ? (selectedInvoice.creditNoteNumber || selectedInvoice.invoiceNumber)
                      : formatInvoiceNumberSafe(selectedInvoice.invoiceNumber, selectedInvoice.financialYear || getFinancialYear(selectedInvoice.invoiceDate), config)
                    }
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn btn-primary" onClick={handlePrint}>
                      <Printer style={{ marginRight: 6 }} />Print Invoice
                    </button>
                    <button className="btn" style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }} onClick={() => setSelectedInvoice(null)} aria-label="Close"><X /></button>
                  </div>
                </div>
                <div style={{ padding: '20px 30px 30px 30px' }}>
                  {selectedInvoice.invoiceType === 'adhoc' ? (
                    <AdhocInvoicePreview
                      client={selectedInvoice.client}
                      invoiceNumber={selectedInvoice.invoiceNumber}
                      invoiceDate={selectedInvoice.invoiceDate}
                      dueDate={selectedInvoice.dueDate}
                      placeOfSupply={selectedInvoice.placeOfSupply}
                      items={selectedInvoice.items}
                      subtotal={selectedInvoice.totals?.subtotal || 0}
                      cgst={selectedInvoice.totals?.cgst || 0}
                      sgst={selectedInvoice.totals?.sgst || 0}
                      roundOff={(() => {
                        const storedRoundOff = Number(selectedInvoice.totals?.roundOff || 0);
                        const grossTotal = Number(selectedInvoice.totals?.subtotal || 0) +
                          Number(selectedInvoice.totals?.cgst || 0) +
                          Number(selectedInvoice.totals?.sgst || 0);
                        const roundedTotal = Math.round(grossTotal);
                        const calculatedRoundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;
                        const storedTotal = Number(selectedInvoice.totals?.total || 0);

                        if (storedRoundOff === 0 && storedTotal === roundedTotal && Math.abs(grossTotal - roundedTotal) > 0.001) {
                          return calculatedRoundOff;
                        }
                        return storedRoundOff !== 0 ? storedRoundOff : (storedTotal === roundedTotal ? calculatedRoundOff : 0);
                      })()}
                      total={selectedInvoice.totals?.total || 0}
                      amountInWords={selectedInvoice.amountInWords}
                      printRef={printRef}
                      handlePrint={handlePrint}
                      config={config}
                      poNumber={selectedInvoice.poNumber}
                      poDate={selectedInvoice.poDate}
                      referenceQuoteNumber={selectedInvoice.reference}
                      showShipTo={selectedInvoice.showShipTo}
                      financialYear={selectedInvoice.financialYear || getFinancialYear(selectedInvoice.invoiceDate)}
                      hideInlinePrintButton
                      bankAccount={selectedInvoice.bankAccount}
                    />
                  ) : (
                    <InvoicePreview
                      client={selectedInvoice.client}
                      invoiceNumber={selectedInvoice.invoiceNumber}
                      invoiceDate={selectedInvoice.invoiceDate}
                      dueDate={selectedInvoice.dueDate}
                      placeOfSupply={selectedInvoice.placeOfSupply}
                      items={selectedInvoice.items}
                      subtotal={selectedInvoice.totals?.subtotal || 0}
                      cgst={selectedInvoice.totals?.cgst || 0}
                      sgst={selectedInvoice.totals?.sgst || 0}
                      roundOff={(() => {
                        const storedRoundOff = Number(selectedInvoice.totals?.roundOff || 0);
                        const grossTotal = Number(selectedInvoice.totals?.subtotal || 0) +
                          Number(selectedInvoice.totals?.cgst || 0) +
                          Number(selectedInvoice.totals?.sgst || 0);
                        const roundedTotal = Math.round(grossTotal);
                        const calculatedRoundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;
                        const storedTotal = Number(selectedInvoice.totals?.total || 0);

                        if (storedRoundOff === 0 && storedTotal === roundedTotal && Math.abs(grossTotal - roundedTotal) > 0.001) {
                          return calculatedRoundOff;
                        }
                        return storedRoundOff !== 0 ? storedRoundOff : (storedTotal === roundedTotal ? calculatedRoundOff : 0);
                      })()}
                      total={selectedInvoice.totals?.total || 0}
                      amountInWords={selectedInvoice.amountInWords}
                      printRef={printRef}
                      handlePrint={handlePrint}
                      config={config}
                      poNumber={selectedInvoice.poNumber}
                      poDate={selectedInvoice.poDate}
                      referenceQuoteNumber={selectedInvoice.reference}
                      showShipTo={selectedInvoice.showShipTo}
                      financialYear={selectedInvoice.financialYear || getFinancialYear(selectedInvoice.invoiceDate)}
                      isCreditNote={selectedInvoice.invoiceType === 'credit_note'}
                      additionalInfo={(() => {
                        const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                          ? selectedInvoice.originalInvoice
                          : (selectedInvoice.originalInvoice ? invoices.find(inv => String(inv._id) === String(selectedInvoice.originalInvoice)) : null);
                        return selectedInvoice.invoiceType === 'credit_note' && origInv
                          ? `This is a credit note made against invoice ${formatInvoiceNumberSafe(origInv.invoiceNumber, origInv.financialYear || getFinancialYear(origInv.invoiceDate), config)}`
                          : undefined;
                      })()}
                      invoiceRefNumber={(() => {
                        const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                          ? selectedInvoice.originalInvoice
                          : (selectedInvoice.originalInvoice ? invoices.find(inv => String(inv._id) === String(selectedInvoice.originalInvoice)) : null);
                        return origInv ? formatInvoiceNumberSafe(origInv.invoiceNumber, origInv.financialYear || getFinancialYear(origInv.invoiceDate), config) : undefined;
                      })()}
                      originalInvoiceDate={(() => {
                        const origInv = typeof selectedInvoice.originalInvoice === 'object' && selectedInvoice.originalInvoice !== null
                          ? selectedInvoice.originalInvoice
                          : (selectedInvoice.originalInvoice ? invoices.find(inv => String(inv._id) === String(selectedInvoice.originalInvoice)) : null);
                        return origInv ? origInv.invoiceDate : undefined;
                      })()}
                      hideQuoteNumber={(selectedInvoice.invoiceType || 'rent') === 'rent'}
                      bankAccount={selectedInvoice.bankAccount}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {
        paymentModalOpen && paymentInvoice && (
          <div className="modal-right-overlay">
            <div className="modal-right-content" onClick={e => e.stopPropagation()}>
              <div className="modal-right-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Payment Details (Multi-Payment)</h2>
                  <button
                    className="btn"
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }}
                    onClick={() => setPaymentModalOpen(false)}
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="modal-right-body">
                <div className="form-group">
                  <strong>Client Name:</strong><br />
                  {paymentInvoice.client?.registeredName || paymentInvoice.client?.name || ''}
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <strong>Invoice Number:</strong><br />
                  {paymentInvoice.invoiceType === 'credit_note'
                    ? (paymentInvoice.creditNoteNumber || paymentInvoice.invoiceNumber)
                    : formatInvoiceNumberSafe(paymentInvoice.invoiceNumber, paymentInvoice.financialYear || getFinancialYear(paymentInvoice.invoiceDate), config)
                  }
                </div>

                {/* Totals Summary */}
                <div style={{ marginTop: 20, padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Invoice Total:</span>
                    <span style={{ fontWeight: 600 }}>{formatIndianCurrency(Number(paymentInvoice.totals?.total || 0))}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Total Received:</span>
                    <span style={{ fontWeight: 600, color: '#059669' }}>{formatIndianCurrency(Number(paymentInvoice.receivedAmount || 0))}</span>
                  </div>
                  {Number(paymentInvoice.tdsPercent) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#64748b' }}>TDS ({paymentInvoice.tdsPercent}%):</span>
                      <span style={{ fontWeight: 600, color: '#dc2626' }}>
                        {formatIndianCurrency(Number(paymentInvoice.totals?.subtotal || 0) * (Number(paymentInvoice.tdsPercent) / 100))}
                      </span>
                    </div>
                  )}
                  {paymentInvoice.invoiceType === 'adhoc' && Number(paymentInvoice.totals?.totalTds || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#64748b' }}>Less: TDS Deducted:</span>
                      <span style={{ fontWeight: 600, color: '#dc2626' }}>
                        -{formatIndianCurrency(paymentInvoice.totals.totalTds)}
                      </span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px dashed #cbd5e1', margin: '8px 0' }}></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>Balance Due:</span>
                    <span style={{ fontWeight: 700, color: '#2563eb' }}>
                      {formatIndianCurrency(
                        Math.max(0,
                          Number(paymentInvoice.totals?.total || 0) -
                          Number(paymentInvoice.receivedAmount || 0) -
                          (paymentInvoice.invoiceType === 'adhoc'
                            ? Number(paymentInvoice.totals?.totalTds || 0)
                            : (Number(paymentInvoice.totals?.subtotal || 0) * (Number(paymentForm.tdsPercent || paymentInvoice.tdsPercent || 0) / 100))
                          )
                        )
                      )}
                    </span>
                  </div>
                </div>

                {/* Payment History Table */}
                {paymentInvoice.payments && paymentInvoice.payments.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: 12 }}>Payment History</h3>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                      <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                          <tr>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentInvoice.payments.map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px' }}>{format(new Date(p.date), 'dd/MM/yy')}</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatIndianCurrency(p.amount)}</td>
                              <td style={{ padding: '8px' }}>{p.paymentMode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #e2e8f0', margin: '24px 0 16px', paddingTop: 16 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: 16 }}>Add New Payment</h3>

                  <div className="form-group">
                    <label>TDS Percentage (%)</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={paymentForm.tdsPercent || null}
                      onChange={(v) => setPaymentForm(f => ({ ...f, tdsPercent: v ?? '' }))}
                      placeholder="e.g., 10"
                      step={0.1}
                      min={0}
                      max={100}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>Amount Received <span style={{ color: 'red' }}>*</span></label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={paymentForm.receivedAmount || null}
                      onChange={(v) => setPaymentForm(f => ({ ...f, receivedAmount: v ?? '' }))}
                      placeholder="Enter amount"
                      step={1}
                      min={0}
                      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>Payment Mode</label>
                    <Select
                      style={{ width: '100%' }}
                      value={paymentForm.paymentMode}
                      onChange={(v) => setPaymentForm(f => ({ ...f, paymentMode: v }))}
                      options={[
                        { value: 'Bank Transfer', label: 'Bank Transfer' },
                        { value: 'Upfront Payment', label: 'Upfront Payment' },
                        { value: 'Bank Remittance', label: 'Bank Remittance' },
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Cheque', label: 'Cheque' },
                        { value: 'Credit Card', label: 'Credit Card' },
                        { value: 'UPI', label: 'UPI' }
                      ]}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>UTR / Reference Number</label>
                    <Input
                      value={paymentForm.utrNumber}
                      onChange={e => setPaymentForm(f => ({ ...f, utrNumber: e.target.value }))}
                      placeholder="e.g. UTR123456789"
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>Payment Date</label>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={paymentForm.receivedDate ? dayjs(paymentForm.receivedDate) : null}
                      onChange={(d) => setPaymentForm(f => ({ ...f, receivedDate: d ? d.format('YYYY-MM-DD') : '' }))}
                      format="DD/MM/YYYY"
                      allowClear={false}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: 16 }}>
                    <label>Notes</label>
                    <Input.TextArea
                      value={paymentForm.paymentNotes}
                      onChange={e => setPaymentForm(f => ({ ...f, paymentNotes: e.target.value }))}
                      placeholder="Optional notes"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-right-footer">
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setPaymentModalOpen(false)}>Close</button>
                  <button className="btn btn-primary" onClick={handleSavePayment}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Invoice Modal */}
      {
        editingInvoice && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              backgroundColor: 'white',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: '8px',
              padding: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Edit {editingInvoice.invoiceType === 'adhoc' ? 'Adhoc' : 'Rent'} Invoice</h2>
                <button onClick={() => setEditingInvoice(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                  <X size="24" />
                </button>
              </div>

              {/* Client Selection */}
              <div className="form-group">
                <label>Client</label>
                <Select
                  style={{ width: '100%' }}
                  value={editForm.client || undefined}
                  onChange={(v) => handleEditFormChange('client', v || '')}
                  placeholder="Select Client"
                  options={clients.map(c => ({ label: c.name, value: c._id }))}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </div>

              {/* Invoice Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label>Invoice Number</label>
                  <input
                    type="text"
                    value={editForm.invoiceNumber}
                    onChange={e => handleEditFormChange('invoiceNumber', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div className="form-group">
                  <label>Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoiceDate}
                    onChange={e => handleEditFormChange('invoiceDate', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div className="form-group">
                  <label>Financial Year</label>
                  <input
                    type="text"
                    value={editForm.financialYear}
                    onChange={e => handleEditFormChange('financialYear', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div className="form-group">
                  <label>Place of Supply</label>
                  <input
                    type="text"
                    value={editForm.placeOfSupply}
                    onChange={e => handleEditFormChange('placeOfSupply', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div className="form-group">
                  <label>PO Number</label>
                  <input
                    type="text"
                    value={editForm.poNumber}
                    onChange={e => handleEditFormChange('poNumber', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div className="form-group">
                  <label>PO Date</label>
                  <input
                    type="date"
                    value={editForm.poDate}
                    onChange={e => handleEditFormChange('poDate', e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
              </div>

              {/* Items Table */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3>Invoice Items</h3>
                  <button onClick={addEditItem} className="btn btn-secondary">
                    <Plus size="1em" style={{ marginRight: 6 }} />Add Item
                  </button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>S.No.</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>HSN/SAC</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>Description</th>
                      {editingInvoice.invoiceType === 'adhoc' ? (
                        <>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Unit Price</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Qty</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px', width: 110 }}>UOM</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Net Amount</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Tax %</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Tax Amount</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Total</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Code Type</th>
                        </>
                      ) : (
                        <>
                          <th style={{ border: '1px solid #ddd', padding: '8px', width: 110 }}>UOM</th>
                          <th style={{ border: '1px solid #ddd', padding: '8px' }}>Amount</th>
                        </>
                      )}
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editForm.items.map((item, index) => {
                      if (editingInvoice.invoiceType === 'adhoc') {
                        const unitPrice = Number(item.unitPrice || 0);
                        const qty = Number(item.qty || 1);
                        const netAmount = unitPrice * qty;
                        const taxRate = Number(item.tax || 0);
                        const taxAmount = netAmount * (taxRate / 100);
                        const totalAmount = netAmount + taxAmount;

                        return (
                          <tr key={index}>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{index + 1}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <HSNSearchDropdown
                                value={item.hsn}
                                onChange={(value) => handleEditItemChange(index, 'hsn', value)}
                                inputClassName="cell-input cell-input--hsn"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => handleEditItemChange(index, 'description', e.target.value)}
                                className="cell-input cell-input--desc"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => handleEditItemChange(index, 'unitPrice', e.target.value)}
                                className="cell-input cell-input--amt"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="number"
                                value={item.qty}
                                onChange={(e) => handleEditItemChange(index, 'qty', e.target.value)}
                                className="cell-input cell-input--amt"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <Select
                                style={{ width: '100%' }}
                                value={item.uom || undefined}
                                onChange={(value) => handleEditItemChange(index, 'uom', value)}
                                placeholder="UOM"
                                options={UOM_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatIndianCurrency(netAmount)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="number"
                                value={item.tax}
                                onChange={(e) => handleEditItemChange(index, 'tax', e.target.value)}
                                className="cell-input cell-input--amt"
                              />%
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatIndianCurrency(taxAmount)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatIndianCurrency(totalAmount)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                              <CodeTypeSelect
                                size="small"
                                value={codeTypeFromIsServc(item.IsServc)}
                                onChange={(value) => handleEditItemChange(index, 'IsServc', isServcFromCodeType(value))}
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {editForm.items.length > 1 && (
                                <button onClick={() => removeEditItem(index)} style={{ background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}>
                                  <Trash size="16" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      } else {
                        return (
                          <tr key={index}>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{index + 1}</td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="text"
                                value={item.hsn}
                                onChange={(e) => handleEditItemChange(index, 'hsn', e.target.value)}
                                className="cell-input cell-input--hsn"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => handleEditItemChange(index, 'description', e.target.value)}
                                className="cell-input cell-input--desc"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <Select
                                style={{ width: '100%' }}
                                value={item.uom || undefined}
                                onChange={(value) => handleEditItemChange(index, 'uom', value)}
                                placeholder="UOM"
                                options={UOM_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              <input
                                type="number"
                                value={item.amount}
                                onChange={(e) => handleEditItemChange(index, 'amount', e.target.value)}
                                className="cell-input cell-input--amt"
                              />
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {editForm.items.length > 1 && (
                                <button onClick={() => removeEditItem(index)} style={{ background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}>
                                  <Trash size="16" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals Display */}
              <div style={{ marginBottom: '20px' }}>
                <h4>Totals</h4>
                {(() => {
                  const { subtotal, cgst, sgst, roundOff, total } = calculateEditTotals();
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                      <div>Subtotal: {formatIndianCurrency(subtotal)}</div>
                      <div>CGST: {formatIndianCurrency(cgst)}</div>
                      <div>SGST: {formatIndianCurrency(sgst)}</div>
                      {roundOff !== 0 && <div>Round Off: {formatIndianCurrency(roundOff)}</div>}
                      <div><strong>Total: {formatIndianCurrency(total)}</strong></div>
                    </div>
                  );
                })()}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setEditingInvoice(null)} className="btn btn-secondary">Cancel</button>
                <button onClick={handleEditSubmit} className="btn btn-primary">Update Invoice</button>
              </div>
            </div>
          </div>
        )
      }

      {/* Reject Modal */}
      <Modal
        title={<div style={{ color: '#ff4d4f', fontWeight: 600 }}>Reject Invoice & Return for Rework</div>}
        open={Boolean(rejectModalRecord)}
        onCancel={() => {
          setRejectModalRecord(null);
          setRejectComment('');
        }}
        onOk={handleConfirmReject}
        okText="Reject Invoice"
        okButtonProps={{ danger: true, loading: workflowLoading }}
        confirmLoading={workflowLoading}
      >
        <p style={{ color: '#666', marginBottom: 12 }}>
          Please provide a reason for rejecting invoice #{rejectModalRecord?.displayInvoiceNumber}. The creator will be able to edit and resubmit it.
        </p>
        <Input.TextArea
          rows={4}
          placeholder="Enter rejection reason (required)..."
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
        />
      </Modal>

      {/* Credit Note Modal */}
      <CreditNoteModal
        isOpen={creditNoteModalOpen}
        onClose={() => setCreditNoteModalOpen(false)}
        originalInvoice={selectedInvoiceForCredit}
        onCreditNoteCreated={(creditNote) => {
          setInvoices(prev => [...prev, creditNote]);
          message.success('Credit note created successfully!');
        }}
      />

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div >
  );
}

export default InvoiceHistory;
